// @flow

import styled from 'styled-components';
import React from 'react';
import { compose } from 'redux';
import type { Node } from 'react';
import { reduxForm } from 'redux-form';
import { connect } from 'react-redux';
import { Form } from '../form';
import {
  submit,
  isSubmitted as isSubmittedSelector,
  hasError as hasErrorSelector,
  type Values,
} from '../../store/form';
import type { Theme } from '../../types';
import { getFormSubmitFailedErrorText } from '../../store/formidableSettings';

const ErrorBar = styled<any, Theme>('section')`
  background-color: ${props => props.theme.form.submitBarError.backgroundColor};
  border-radius: ${props => props.theme.form.submitBarError.borderRadius};
  color: ${props => props.theme.form.submitBarError.color};
  margin-bottom: 1rem;
  padding: 1rem;
`;

const SuccessBar = styled<any, Theme>('section')`
  background-color: ${props => props.theme.form.submitBarSuccess.backgroundColor};
  border-radius: ${props => props.theme.form.submitBarSuccess.borderRadius};
  color: ${props => props.theme.form.submitBarSuccess.color};
  margin-top: 1rem;
  padding: 1rem;
`;

/**
 * We defined some of the ReduxForm properties in our OwnProps object.
 * Reason is that the FormProps export of ReduxForm is not working correctly.
 *
 * See https://redux-form.com/7.3.0/docs/api/reduxform.md/ for available props.
 */
type OwnProps = {|
  children: Node,
  className?: string,
  form: string,
  successMsg: string,
  id?: string,
  initialValues?: { // eslint-disable-line react/no-unused-prop-types
    [string]: string,
  },
  onSuccess?: Function,
|};

type StateProps = {|
  isSubmitted: (formName: string) => boolean,
  hasError: (formName: string) => boolean,
  formSubmitFailedErrorText: string,
|};

type DispatchProps = {|
  formSubmit: Function,
|};

type FormProps = {
  handleSubmit: (Function) => void,
  formSubmit: Function,
};

type Props = OwnProps & FormProps & DispatchProps & StateProps;

const Formidable = ({
  children,
  className,
  form,
  handleSubmit, // this one is injected by redux form
  formSubmit,
  id,
  isSubmitted,
  hasError,
  successMsg,
  formSubmitFailedErrorText,
  onSuccess,
}: Props) => {
  const formName = id || form;
  return (
    <>
      {!isSubmitted(formName) && (
        <Form
          className={className}
          id={id || form}
          onSubmit={handleSubmit((values: { [string]: string }) => formSubmit(formName, values))}
          data-cy="formidableForm"
        >
          {children}
        </Form>
      )}
      {isSubmitted(formName) && onSuccess && (
        <SuccessBar>{successMsg}</SuccessBar>
      )}
      {hasError(formName)
        && <ErrorBar>{formSubmitFailedErrorText}</ErrorBar>}
    </>
  );
};

Formidable.defaultProps = {
  className: '',
  id: '',
  initialValues: {},
  onSuccess: () => {},
};

const mapStateToProps = (state): StateProps => ({
  isSubmitted: (formName: string) => isSubmittedSelector(state, formName),
  hasError: (formName: string) => hasErrorSelector(state, formName),
  formSubmitFailedErrorText: getFormSubmitFailedErrorText(state),
});

const mapDispatchToProps = (dispatch: Function, { fields, onSuccess }): DispatchProps => ({
  formSubmit: (id: string, values: Values) => dispatch(submit(id, values, fields)).then(onSuccess),
});

export default compose(
  reduxForm(),
  connect(mapStateToProps, mapDispatchToProps),
)(Formidable);
